<template>
    <div>
        <AdminPageHeader
            title="Catálogo de productos 3D"
            description="Piezas publicadas en el catálogo público, su material y su precio de partida."
        />

        <div class="catalogo__layout">
            <div class="adm-stack">
                <AdmCard :title="selected ? `Editando producto #${selected.id}` : 'Nuevo producto'">
                    <ProductoForm
                        :value="selected || {}"
                        :materials="materials"
                        :categories="categories"
                        :editing="!!selected"
                        @submit="onSubmit"
                        @cancel="resetSelection"
                    />
                    <template v-if="selected" #footer>
                        <AdmButton variant="danger" :icon="Trash2" @click="onDelete(selected.id)">
                            Eliminar producto
                        </AdmButton>
                    </template>
                </AdmCard>
            </div>

            <div class="adm-stack">
                <AdmCard title="Filtros">
                    <div class="catalogo__filters">
                        <AdmField v-model="filter.name" label="Nombre" placeholder="Filtrar por nombre" />

                        <AdmField v-model="filter.materialId" label="Material" type="select">
                            <option value="">Todos los materiales</option>
                            <option v-for="m in materials" :key="m.id" :value="m.id">{{ m.name }}</option>
                        </AdmField>

                        <AdmField v-model="filter.categoryId" label="Categoría" type="select">
                            <option value="">Todas las categorías</option>
                            <option value="none">Sin categoría</option>
                            <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                        </AdmField>

                        <div class="catalogo__checks">
                            <label class="catalogo__check">
                                <input v-model="filter.isFeatured" type="checkbox" /> Sólo principales
                            </label>
                            <label class="catalogo__check">
                                <input v-model="filter.includeInactive" type="checkbox" /> Incluir inactivos
                            </label>
                        </div>
                    </div>
                </AdmCard>

                <AdmCard title="Productos" flush>
                    <AdmTable
                        :columns="columns"
                        :rows="items"
                        :selected-id="selected?.id ?? null"
                        empty-message="No hay productos que coincidan con el filtro."
                    >
                        <template #cell-name="{ row }">
                            {{ row.name }}
                            <AdmBadge v-if="!row.isActive" tone="neutral" label="Inactivo" />
                        </template>

                        <template #cell-isFeatured="{ row }">
                            <!-- Marcar la temporada es la operación más frecuente del
                                 catálogo, así que se hace desde la fila sin abrir la ficha. -->
                            <button
                                type="button"
                                class="catalogo__star"
                                :class="{ 'is-on': row.isFeatured }"
                                :title="row.isFeatured ? 'Quitar del catálogo principal' : 'Mostrar en el catálogo principal'"
                                :aria-pressed="row.isFeatured ? 'true' : 'false'"
                                @click="toggleFeatured(row)"
                            >
                                <Star :size="17" :fill="row.isFeatured ? 'currentColor' : 'none'" />
                            </button>
                        </template>

                        <template #cell-actions="{ row }">
                            <AdmButton variant="secondary" size="sm" :icon="Pencil" @click="select(row)">
                                Editar
                            </AdmButton>
                        </template>
                    </AdmTable>

                    <template #footer>
                        <AdmPagination :page="page" :total-pages="totalPages" @update:page="goToPage" />
                    </template>
                </AdmCard>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Catálogo de productos 3D — migrada en el plan 27_09_2026 (F3/T20).
 *
 * Se retiran la barra `#menu` duplicada, la tercera definición propia de
 * `.data-table` y el `.page` con su `max-width` de 1300px. La carga de datos
 * (`getProducts` + `getMaterials` + `getProductCategories` en paralelo) queda
 * igual que estaba.
 */
import { Trash2, Pencil, Star } from 'lucide-vue-next';
import ProductoForm from './ProductoForm.vue';
import { formatCurrencyRAE } from '@/utils/numberFormat';
import {
    getProducts, saveProduct, editProduct, deleteProduct, getMaterials,
    getProductCategories,
} from '@/services/dbPrint3dAdmin.js';
import AdminPageHeader from '@/components/Admin/AdminPageHeader.vue';
import AdmCard from '@/components/Admin/ui/AdmCard.vue';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import AdmTable from '@/components/Admin/ui/AdmTable.vue';
import AdmBadge from '@/components/Admin/ui/AdmBadge.vue';
import AdmPagination from '@/components/Admin/ui/AdmPagination.vue';
import { notify } from '@/composables/useAdmToast.js';

export default {
    name: 'Print3DCatalogoPage',
    components: {
        ProductoForm, AdminPageHeader, AdmCard, AdmField,
        AdmButton, AdmTable, AdmBadge, AdmPagination, Star,
    },
    data() {
        return {
            items: [],
            materials: [],
            categories: [],
            selected: null,
            page: 1,
            limit: 10,
            totalPages: 1,
            filter: { name: '', materialId: '', categoryId: '', isFeatured: false, includeInactive: false },
            debounceTimer: null,
            Trash2, Pencil, Star,
        };
    },
    computed: {
        columns() {
            return [
                { key: 'id', label: '#', width: '60px' },
                { key: 'name', label: 'Nombre' },
                { key: 'categoryId', label: 'Categoría', format: (v) => this.categoryName(v) },
                { key: 'isFeatured', label: 'Principal', align: 'center', width: '90px' },
                { key: 'materialId', label: 'Material', format: (v) => this.materialName(v) },
                { key: 'size', label: 'Tamaño (mm)', format: (_, row) => `${row.sizeMmX} × ${row.sizeMmY} × ${row.sizeMmZ}` },
                { key: 'priceFrom', label: 'Costo desde', align: 'right', format: (v, row) => this.formatCurrency(v, row.currency) },
                { key: 'displayOrder', label: 'Orden', align: 'right', width: '80px' },
                { key: 'actions', label: 'Acciones', align: 'right', width: '110px' },
            ];
        },
    },
    watch: {
        filter: {
            deep: true,
            handler() {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = setTimeout(() => {
                    this.page = 1;
                    this.fetchItems();
                }, 350);
            },
        },
    },
    methods: {
        async fetchItems() {
            const offset = (this.page - 1) * this.limit;
            // Las categorías se piden con inactivas incluidas: un producto puede
            // seguir apuntando a una desactivada y su nombre debe verse igual.
            const [{ items, totalPages }, materialsRes, categoriesRes] = await Promise.all([
                getProducts(offset, this.limit, this.filter),
                getMaterials(0, 100, {}),
                getProductCategories(true),
            ]);
            this.items = items;
            this.totalPages = totalPages;
            this.materials = materialsRes.items;
            this.categories = categoriesRes.categories;
        },
        goToPage(page) { this.page = page; this.fetchItems(); },
        categoryName(id) {
            if (!id) return 'Sin categoría';
            const c = this.categories.find((x) => x.id === id);
            return c ? c.name : `#${id}`;
        },
        materialName(id) {
            const m = this.materials.find((x) => x.id === id);
            return m ? m.name : `#${id}`;
        },
        async toggleFeatured(product) {
            const { error } = await editProduct(product.id, { isFeatured: !product.isFeatured });
            if (error) {
                notify.error(`Error al cambiar el destacado: ${error}`);
                return;
            }
            // Optimista sobre la fila, sin recargar toda la página, salvo que el
            // filtro "sólo principales" esté activo y la fila deba desaparecer.
            product.isFeatured = !product.isFeatured;
            if (this.filter.isFeatured) this.fetchItems();
        },
        select(p) { this.selected = { ...p }; },
        resetSelection() { this.selected = null; },
        async onSubmit(payload) {
            if (this.selected) {
                const { error } = await editProduct(this.selected.id, payload);
                if (error) {
                    notify.error(`Error al editar: ${error}`);
                    return;
                }
                notify.success('Producto actualizado.');
            } else {
                const { error } = await saveProduct(payload);
                if (error) {
                    notify.error(`Error al crear: ${error}`);
                    return;
                }
                notify.success('Producto creado.');
            }
            this.resetSelection();
            this.fetchItems();
        },
        async onDelete(id) {
            if (!confirm('¿Eliminar este producto? (soft-delete)')) return;
            const { error } = await deleteProduct(id);
            if (error) {
                notify.error(`Error al eliminar: ${error}`);
                return;
            }
            notify.success('Producto eliminado.');
            this.resetSelection();
            this.fetchItems();
        },
        formatCurrency(value, currency = 'COP') {
            return formatCurrencyRAE(value, currency);
        },
    },
    mounted() { this.fetchItems(); },
};
</script>

<style scoped>
/* La ficha de producto es estrecha y la tabla necesita todo el ancho que
   pueda: por eso no se usa `.adm-grid-2` (mitades iguales) aquí. */
.catalogo__layout {
    display: grid;
    grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
    gap: var(--adm-gap-lg);
    align-items: start;
}

@media (max-width: 1200px) {
    .catalogo__layout { grid-template-columns: minmax(0, 1fr); }
}

.catalogo__filters {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--adm-gap);
    align-items: end;
}

.catalogo__checks {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.catalogo__check {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: var(--adm-font-sm);
    color: var(--color-text);
}

.catalogo__star {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.2rem;
    background: none;
    border: none;
    border-radius: var(--adm-radius-sm);
    color: var(--adm-border-strong);
    cursor: pointer;
}

.catalogo__star.is-on { color: var(--adm-star); }

.catalogo__star:hover { color: var(--color-brand); }
</style>
